import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../services/supabaseClient'
import { useAppSelector } from '../../store/hooks'
import { SECTIONS } from '../../data/sections'
import type { SectionId } from '../../types/tools'
import type { Profile } from '../../store/authSlice'
import styles from './AdminPanel.module.css'

interface CompletionMatrixProps {
  profiles: Profile[]
}

interface ToolEntry { id: string; label: string }

function getAllTools(): ToolEntry[] {
  const tools: ToolEntry[] = []
  for (const sk of Object.keys(SECTIONS) as SectionId[]) {
    const cats = SECTIONS[sk].cats as unknown as Record<string, { tools: Record<string, { l: string }> }>
    for (const ck of Object.keys(cats)) {
      for (const tk of Object.keys(cats[ck].tools)) {
        tools.push({ id: tk, label: cats[ck].tools[tk].l })
      }
    }
  }
  return tools
}

interface CompletionRow {
  name: string
  completed: Set<string>
}

export default function CompletionMatrix({ profiles }: CompletionMatrixProps) {
  const adminTeamId = useAppSelector((s) => s.auth.profile?.team_id ?? null)
  const [rows, setRows] = useState<CompletionRow[]>([])
  const [loading, setLoading] = useState(true)
  const allTools = getAllTools()

  // RLS now scopes responses to the admin's team — only show profiles
  // whose data the admin can actually see, so the matrix isn't full of
  // misleading empty rows for out-of-team users.
  const visibleProfiles = useMemo(
    () => (adminTeamId ? profiles.filter((p) => p.team_id === adminTeamId) : []),
    [profiles, adminTeamId]
  )

  useEffect(() => {
    async function load() {
      setLoading(true)

      if (!adminTeamId) {
        setRows([])
        setLoading(false)
        return
      }

      const { data: responses } = await supabase
        .from('responses')
        .select('user_id, tool_id')

      const responseMap = new Map<string, Set<string>>()
      responses?.forEach((r) => {
        if (!responseMap.has(r.user_id)) responseMap.set(r.user_id, new Set())
        responseMap.get(r.user_id)!.add(r.tool_id)
      })

      const completionRows: CompletionRow[] = visibleProfiles.map((p) => ({
        name: p.display_name ?? p.email,
        completed: responseMap.get(p.id) ?? new Set(),
      }))

      setRows(completionRows)
      setLoading(false)
    }

    load()
  }, [visibleProfiles, adminTeamId])

  if (loading) {
    return (
      <div className={styles.card} data-testid="completion-matrix-loading">
        <div className={styles.hdrBlue}>Completion Overview</div>
        <div className={styles.loadingText}>Loading...</div>
      </div>
    )
  }

  if (!adminTeamId) {
    return (
      <div className={styles.card} data-testid="completion-matrix-no-team">
        <div className={styles.hdrBlue}>Completion Overview</div>
        <div className={styles.loadingText}>
          Join a team to see completion data. You can only view responses from members of teams you belong to.
        </div>
      </div>
    )
  }

  return (
    <div className={styles.card} data-testid="completion-matrix">
      <div className={styles.hdrBlue}>Completion Overview</div>
      <div className={styles.matrixWrap}>
        <table className={styles.matrixTable}>
          <thead>
            <tr>
              <th className={styles.matrixNameCol}>Name</th>
              {allTools.map((t) => (
                <th key={t.id} className={styles.matrixToolCol} title={t.label}>
                  <div className={styles.matrixToolLabel}>{t.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name}>
                <td className={styles.matrixName}>{r.name}</td>
                {allTools.map((t) => (
                  <td key={t.id} className={styles.matrixCell} title={t.label}>
                    {r.completed.has(t.id) ? '✓' : '•'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
